import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n';
import './styles/tokens.css';
import './styles/touch-targets.css';
import App from './App';
import { OverlayProvider } from './components/overlay/OverlayProvider';

// Theme initialization (runs before first paint to prevent flash)
// Precedence: explicit user choice (localStorage) > OS preference (prefers-color-scheme) > light (root default)
(function initTheme() {
  const storedTheme = localStorage.getItem('aif_theme');
  const root = document.documentElement;

  // Clear any pre-existing theme classes (defensive)
  root.classList.remove('dark', 'light', 'outdoor');

  if (storedTheme === 'dark') {
    root.classList.add('dark');
  } else if (storedTheme === 'light') {
    root.classList.add('light');
  } else if (storedTheme === 'outdoor') {
    root.classList.add('outdoor');
  } else {
    // No stored preference: check OS via prefers-color-scheme
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
    }
    // else: light mode (the :root default in tokens.css handles it; no class needed)
  }

  // Update meta theme-color to match current theme
  updateThemeColor(root);
})();

function updateThemeColor(root) {
  const isDark = root.classList.contains('dark') ||
    (root.classList.length === 0 && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isOutdoor = root.classList.contains('outdoor');

  let color = '#ffffff';  // light default
  if (isOutdoor) color = '#ffffff';
  else if (isDark) color = '#0f0f1a';  // matches --aif-bg-page in .dark

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', color);
}

// Listen for OS theme changes and update when user hasn't set explicit preference
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
  const storedTheme = localStorage.getItem('aif_theme');
  if (storedTheme) return;  // explicit choice wins

  const root = document.documentElement;
  root.classList.remove('dark', 'light');
  if (e.matches) root.classList.add('dark');
  updateThemeColor(root);
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ componentStack: errorInfo?.componentStack || null });
  }
  render() {
    if (this.state.error) {
      const errStr = String(this.state.error);
      const stack = this.state.error?.stack || '';
      const compStack = this.state.componentStack || '';
      return React.createElement('div', {
        style: {
          position: 'fixed', inset: 0, background: '#030712', color: '#f87171',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: 24, fontFamily: 'monospace', zIndex: 'var(--aif-z-critical)',
          overflow: 'auto',
        }
      },
        React.createElement('div', { style: { fontSize: 18, marginBottom: 12 } }, 'App Error'),
        React.createElement('pre', {
          style: {
            background: '#111', padding: 16, borderRadius: 8, maxWidth: '90vw',
            overflow: 'auto', fontSize: 12, color: '#fca5a5', whiteSpace: 'pre-wrap',
          }
        }, errStr + '\n\n' + stack),
        compStack && React.createElement('pre', {
          style: {
            background: '#1a1a2e', padding: 16, borderRadius: 8, maxWidth: '90vw',
            overflow: 'auto', fontSize: 11, color: '#93c5fd', whiteSpace: 'pre-wrap',
            marginTop: 12,
          }
        }, 'Component Stack:' + compStack),
        React.createElement('button', {
          onClick: () => window.location.reload(),
          style: {
            marginTop: 16, padding: '8px 20px', background: '#22c55e', color: '#000',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
          }
        }, 'Reload')
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <OverlayProvider>
        <App />
      </OverlayProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((registration) => {
      // A6-5: Listen for SW updates (new version installed).
      // On update, the new SW activates immediately via skipWaiting + clients.claim
      // (defined in sw.js). Surface to console for observability during rollout.
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
              console.info('[SW] New version activated:', registration.active?.scriptURL);
            }
          });
        }
      });
    }).catch((error) => {
      // SW registration failure is non-fatal (app works without SW), so we
      // don't throw, but we do log so it's visible in dev tools.
      console.warn('[SW] Registration failed:', error);
    });
  });
}
